import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import { useSection, useSeo } from "@/contexts/SiteContentContext";
import { FreeProfileCTA } from "@/components/interest/ProfileCTAs";
import { Search } from "lucide-react";

export default function Prospects() {
  const c = useSection("prospects");
  useSeo(c.seo_title, c.meta_description);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchItems = (query = "") => {
    setLoading(true);
    api
      .get("/players", { params: query ? { q: query, limit: 60 } : { limit: 60 } })
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    fetchItems(q);
  };

  return (
    <PublicLayout>
      <section className="bg-[#0A192F] text-white py-16 border-b-4 border-[#E63946]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="label-overline text-[#E63946]">{c.overline || "Database"}</div>
          <h1 className="font-display text-5xl lg:text-6xl uppercase tracking-tighter mt-4">{c.title || "CBR Prospects"}</h1>
          <p className="mt-4 text-white/70 max-w-2xl">
            {c.subtitle || "Search the official database of Canadian amateur baseball prospects. Profiles populate with verified metrics and rankings as players attend CBR events."}
          </p>
          <form onSubmit={onSubmit} className="mt-8 flex gap-2 max-w-2xl">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
              <input
                data-testid="prospects-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, school, or team…"
                className="w-full bg-white/10 border border-white/20 rounded-sm pl-10 pr-4 py-3 text-sm placeholder:text-white/40 focus:bg-white/15"
              />
            </div>
            <button data-testid="prospects-search-button" className="bg-[#E63946] px-6 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm">
              Search
            </button>
          </form>
        </div>
      </section>

      <section className="bg-[#F8F9FA] py-12 min-h-[400px]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <FreeProfileCTA className="mb-8" />
          {loading ? (
            <div className="label-overline">Loading…</div>
          ) : items.length === 0 ? (
            <div className="bg-white border border-slate-200 p-10 text-center">
              <div className="font-display text-xl uppercase">No prospects yet</div>
              <p className="text-sm text-slate-500 mt-2">Players appear here as they register and attend CBR events.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left p-4 label-overline">Player</th>
                    <th className="text-left p-4 label-overline">Class</th>
                    <th className="text-left p-4 label-overline">Position</th>
                    <th className="text-left p-4 label-overline">Province</th>
                    <th className="text-left p-4 label-overline">School</th>
                    <th className="text-left p-4 label-overline">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr key={p.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                      <td className="p-4">
                        <Link to={`/prospects/${p.slug}`} className="font-semibold hover:text-[#E63946]" data-testid={`prospect-row-${p.slug}`}>
                          {p.first_name} {p.last_name}
                        </Link>
                      </td>
                      <td className="p-4 font-data">{p.graduation_year || "—"}</td>
                      <td className="p-4 font-data">{p.primary_position || "—"}</td>
                      <td className="p-4">{p.province || "—"}</td>
                      <td className="p-4">{p.school || "—"}</td>
                      <td className="p-4">
                        <span className="bg-slate-100 text-slate-700 text-[10px] font-semibold uppercase tracking-wider px-2 py-1">
                          {p.recruiting?.commitment_status || "Uncommitted"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
